import type { VolWorkspaceDocument } from "../modules/curves/workspace/volWorkstationTypes";

const BASE = "/api/v1/vol-workstation-layouts";

function authHeaders(token: string | null): HeadersInit {
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

export type VolWorkstationPublicLayout = {
  layout_id: string;
  name: string;
  scope_curve_id: string | null;
  data: VolWorkspaceDocument;
  is_public: boolean;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
};

export async function listVolWorkstationLayouts(
  token: string | null,
  scopeCurveId?: string,
): Promise<VolWorkstationPublicLayout[]> {
  const q = scopeCurveId ? `?scope_curve_id=${encodeURIComponent(scopeCurveId)}` : "";
  const r = await fetch(`${BASE}${q}`, {
    headers: authHeaders(token),
    credentials: "omit",
  });
  if (!r.ok) throw new Error(`list vol layouts: ${r.status}`);
  const body = (await r.json()) as { layouts?: VolWorkstationPublicLayout[] };
  return body.layouts ?? [];
}

export async function createVolWorkstationLayout(
  token: string | null,
  payload: {
    name: string;
    scope_curve_id?: string;
    data: VolWorkspaceDocument;
    is_public?: boolean;
  },
): Promise<VolWorkstationPublicLayout> {
  const r = await fetch(BASE, {
    method: "POST",
    headers: authHeaders(token),
    credentials: "omit",
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `create vol layout: ${r.status}`);
  }
  return (await r.json()) as VolWorkstationPublicLayout;
}

export async function updateVolWorkstationLayout(
  token: string | null,
  layoutId: string,
  payload: {
    name?: string;
    data?: VolWorkspaceDocument;
    is_public?: boolean;
  },
): Promise<VolWorkstationPublicLayout> {
  const r = await fetch(`${BASE}/${layoutId}`, {
    method: "PUT",
    headers: authHeaders(token),
    credentials: "omit",
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `update vol layout: ${r.status}`);
  }
  return (await r.json()) as VolWorkstationPublicLayout;
}

export async function deleteVolWorkstationLayout(
  token: string | null,
  layoutId: string,
): Promise<void> {
  const r = await fetch(`${BASE}/${layoutId}`, {
    method: "DELETE",
    headers: authHeaders(token),
    credentials: "omit",
  });
  if (!r.ok && r.status !== 404) {
    const err = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `delete vol layout: ${r.status}`);
  }
}
